import { useEffect, useMemo, useState } from 'react'
import { validateWorkflowConfig } from '@kfolks/models'
import type { WorkflowConfig } from '@kfolks/types'

/**
 * Debounced live validation of the in-progress `WorkflowConfig` against
 * the shared `validateWorkflowConfig` from `@kfolks/models` — the same
 * validator the backend runs on save (per ADR-0012's linear step-array
 * contract: reachability via `after.next`, terminal-target resolution,
 * approver-dimension presence, no-path-to-terminal detection).
 *
 * Returns errors partitioned into per-step and top-level slots so
 * consumers can render them where they're useful:
 *
 *   - `errorsByStepId[id]`: errors whose path is `steps[<n>].*` for the
 *     step with that id. Consumed by `WorkflowCanvas` (per-card error
 *     ring) and `NodePropertiesPanel` (inline banner per editor).
 *   - `topLevelErrors`: everything else (`steps` itself when empty,
 *     "no path to terminal", `version`, etc.). Consumed by the
 *     `ValidationBadge` at the top of the canvas.
 *
 * Debounced at 250 ms (same window as the form-builder's
 * `useFormSchemaValidation`). Unlike the form-builder version, this
 * hook needs **no `useQuery`** — `validateWorkflowConfig` is dep-free,
 * so the workflow builder's state provider can mount it directly
 * without a `QueryClientProvider` ancestor.
 *
 * **Do not** duplicate this hook into other consumers. Mount it once
 * at the top level (in `WorkflowBuilderPage`) and pass results down
 * via shared state. Two parallel debouncers would drift.
 */
export interface WorkflowConfigValidationView {
	/** Per-step error messages, keyed by step id. */
	errorsByStepId: Record<string, string[]>
	/** Errors not attached to a specific step (steps[] empty, no-path-to-terminal, version, etc.). */
	topLevelErrors: string[]
	/** True when the validator has at least one severity-`error` entry. */
	hasErrors: boolean
	/** True between an edit and the debounced re-validation (UX hint). */
	isPending: boolean
}

const EMPTY_VIEW: WorkflowConfigValidationView = {
	errorsByStepId: {},
	topLevelErrors: [],
	hasErrors: false,
	isPending: false,
}

export function useWorkflowConfigValidation(workflowConfig: WorkflowConfig): WorkflowConfigValidationView {
	const [debouncedConfig, setDebouncedConfig] = useState<WorkflowConfig>(workflowConfig)
	const [isPending, setIsPending] = useState(false)
	useEffect(() => {
		setIsPending(true)
		const handle = window.setTimeout(() => {
			setDebouncedConfig(workflowConfig)
			setIsPending(false)
		}, 250)
		return () => window.clearTimeout(handle)
	}, [workflowConfig])

	return useMemo<WorkflowConfigValidationView>(() => {
		// Empty config is a valid edit-time state (admin just opened a
		// blank canvas; the validator would emit "must declare at least
		// one step" which is correct but noisy until the admin actually
		// drops a step). Suppress validation for the empty case.
		if (debouncedConfig.steps.length === 0) {
			return { ...EMPTY_VIEW, isPending }
		}

		const result = validateWorkflowConfig(debouncedConfig)
		const errorsByStepId: Record<string, string[]> = {}
		const topLevelErrors: string[] = []
		for (const err of result.errors) {
			const stepId = extractStepIdFromPath(err.path, debouncedConfig)
			if (stepId !== null) {
				const list = errorsByStepId[stepId] ?? []
				list.push(err.message)
				errorsByStepId[stepId] = list
			} else {
				topLevelErrors.push(err.message)
			}
		}
		return {
			errorsByStepId,
			topLevelErrors,
			hasErrors: result.errors.some((e) => e.severity === 'error'),
			isPending,
		}
	}, [debouncedConfig, isPending])
}

/**
 * Translate a `ValidationError.path` like `steps[2]` or `steps[2].after.next`
 * into the corresponding step id by looking up the index in the config.
 * Returns `null` for top-level paths (`''`, `'version'`, `'steps'`).
 */
function extractStepIdFromPath(path: string, config: WorkflowConfig): string | null {
	const match = /^steps\[(\d+)\]/.exec(path)
	if (!match) return null
	const idx = Number.parseInt(match[1] ?? '', 10)
	if (Number.isNaN(idx)) return null
	const step = config.steps[idx]
	return step?.id ?? null
}
